import type { AnalyticsEvent, AnalyticsEventName } from './events'

export const ANALYTICS_SCHEMA_VERSION = 1 as const
export const MAX_ANALYTICS_BATCH_SIZE = 25
export const MAX_ANALYTICS_BODY_BYTES = 48 * 1024
export const MAX_ANALYTICS_STRING_LENGTH = 64

const ANALYTICS_EVENT_NAMES: readonly AnalyticsEventName[] = [
  'PAGE_VIEW',
  'LANG_DETECTED',
  'LANG_OVERRIDE',
  'GPS_REQUEST',
  'GPS_GRANTED',
  'GPS_DENIED',
  'TEXT_SEARCH',
  'VOICE_START',
  'VOICE_PROVIDER_ATTEMPT',
  'VOICE_SUCCESS',
  'VOICE_FAIL',
  'DESTINATION_SEARCH',
  'CANDIDATE_SHOWN',
  'CANDIDATE_PICKED',
  'DESTINATION_CONFIRMED',
  'FARE_ESTIMATE_VIEW',
  'PROVIDER_COMPARE_VIEW',
  'KRIDE_SELECTED',
  'KRIDE_CLICK',
  'KRIDE_LINK_ERROR',
  'UBER_SELECTED',
  'UBER_CLICK',
  'UBER_LINK_ERROR',
  'PROVIDER_NO_SELECTION_EXIT',
  'PROVIDER_PAGE_HIDDEN',
  'PROVIDER_PAGE_RETURNED',
  'KRIDE_RETURN_SURVEY_VIEW',
  'KRIDE_OPENED_SELF_REPORT',
  'KRIDE_INSTALL_SCREEN_SELF_REPORT',
  'KRIDE_OPEN_FAILED_SELF_REPORT',
  'KRIDE_UNKNOWN_SELF_REPORT',
]

export const REMOTE_METADATA_KEYS = [
  'accuracyStored',
  'available',
  'coordinateStatus',
  'count',
  'detectedLanguage',
  'durationMs',
  'errorCode',
  'estimatedFareBand',
  'krideEnabled',
  'kridePosition',
  'language',
  'locationAvailable',
  'method',
  'mode',
  'outcome',
  'provider',
  'providerPosition',
  'providerVariant',
  'queryLength',
  'reason',
  'region',
  'selectedLanguage',
  'signal',
  'source',
  'transcriptLength',
  'uberEnabled',
  'version',
  'voiceProvider',
] as const

export type RemoteMetadataKey = typeof REMOTE_METADATA_KEYS[number]
export type RemoteMetadataValue = string | number | boolean
export type RemoteAnalyticsMetadata = Partial<Record<RemoteMetadataKey, RemoteMetadataValue>>

export interface RemoteAnalyticsEvent {
  name: AnalyticsEventName
  timestamp: string
  metadata: RemoteAnalyticsMetadata
}

export interface RemoteAnalyticsBatch {
  schemaVersion: typeof ANALYTICS_SCHEMA_VERSION
  sessionId: string
  appVersion: string
  device: {
    os: string
    browser: string
  }
  events: RemoteAnalyticsEvent[]
}

const eventNameSet = new Set<string>(ANALYTICS_EVENT_NAMES)
const metadataKeySet = new Set<string>(REMOTE_METADATA_KEYS)
const sessionIdPattern = /^[A-Za-z0-9._:-]{8,96}$/
const categoricalValuePattern = /^[A-Za-z0-9._:-]{1,64}$/
const booleanMetadataKeys = new Set<RemoteMetadataKey>([
  'accuracyStored', 'available', 'krideEnabled', 'locationAvailable', 'uberEnabled',
])
const numericMetadataRanges: Partial<Record<RemoteMetadataKey, readonly [number, number]>> = {
  count: [0, 1_000],
  durationMs: [0, 120_000],
  queryLength: [0, 10_000],
  transcriptLength: [0, 10_000],
}
const allowedOperatingSystems = new Set(['iOS', 'Android', 'Windows', 'macOS', 'Other'])
const allowedBrowsers = new Set([
  'Samsung Internet', 'Chrome iOS', 'Firefox iOS', 'Edge', 'Chrome', 'Safari', 'Other',
])

export function isAnalyticsEventName(value: unknown): value is AnalyticsEventName {
  return typeof value === 'string' && eventNameSet.has(value)
}

export function isSafeAnalyticsSessionId(value: unknown): value is string {
  return typeof value === 'string' && sessionIdPattern.test(value)
}

function sanitizeRemoteValue(key: RemoteMetadataKey, value: unknown): RemoteMetadataValue | undefined {
  if (booleanMetadataKeys.has(key)) return typeof value === 'boolean' ? value : undefined

  const numericRange = numericMetadataRanges[key]
  if (numericRange) {
    return typeof value === 'number'
      && Number.isFinite(value)
      && value >= numericRange[0]
      && value <= numericRange[1]
      ? value
      : undefined
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()
    return categoricalValuePattern.test(trimmed)
      ? trimmed.slice(0, MAX_ANALYTICS_STRING_LENGTH)
      : undefined
  }
  return undefined
}

/**
 * Converts local debug metadata to the deliberately small remote allow-list.
 * Destination IDs, queries, transcripts, URLs, coordinates and nested values
 * are omitted even if a future call site accidentally supplies them.
 */
export function sanitizeMetadataForRemote(metadata: Record<string, unknown>): RemoteAnalyticsMetadata {
  const safe: RemoteAnalyticsMetadata = {}
  for (const key of REMOTE_METADATA_KEYS) {
    const value = sanitizeRemoteValue(key, metadata[key])
    if (value !== undefined) safe[key] = value
  }
  return safe
}

export function toRemoteAnalyticsEvent(event: AnalyticsEvent): RemoteAnalyticsEvent {
  return {
    name: event.name,
    timestamp: event.timestamp,
    metadata: sanitizeMetadataForRemote(event.metadata),
  }
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function isValidMetadata(value: unknown): value is RemoteAnalyticsMetadata {
  if (!isPlainObject(value)) return false
  const entries = Object.entries(value)
  return entries.length <= REMOTE_METADATA_KEYS.length && entries.every(([key, item]) => (
    metadataKeySet.has(key)
    && sanitizeRemoteValue(key as RemoteMetadataKey, item) === item
  ))
}

function isRecentIsoTimestamp(value: unknown, nowMs: number): value is string {
  if (typeof value !== 'string' || value.length > 32) return false
  const timestampMs = Date.parse(value)
  if (!Number.isFinite(timestampMs)) return false
  return timestampMs >= nowMs - 24 * 60 * 60 * 1000 && timestampMs <= nowMs + 5 * 60 * 1000
}

export function parseRemoteAnalyticsBatch(value: unknown, nowMs = Date.now()): RemoteAnalyticsBatch | null {
  if (!isPlainObject(value)
    || value.schemaVersion !== ANALYTICS_SCHEMA_VERSION
    || !isSafeAnalyticsSessionId(value.sessionId)
    || typeof value.appVersion !== 'string'
    || value.appVersion.length < 1
    || value.appVersion.length > 32
    || !categoricalValuePattern.test(value.appVersion)
    || !isPlainObject(value.device)
    || !allowedOperatingSystems.has(String(value.device.os))
    || !allowedBrowsers.has(String(value.device.browser))
    || !Array.isArray(value.events)
    || value.events.length < 1
    || value.events.length > MAX_ANALYTICS_BATCH_SIZE) {
    return null
  }

  for (const event of value.events) {
    if (!isPlainObject(event)
      || !isAnalyticsEventName(event.name)
      || !isRecentIsoTimestamp(event.timestamp, nowMs)
      || !isValidMetadata(event.metadata)) {
      return null
    }
  }

  return value as unknown as RemoteAnalyticsBatch
}
